import type { ApplicationDraft } from "../../api/client";
import { BooleanChoice } from "../../components/BooleanChoice";
import { TextAreaField } from "../../components/TextAreaField";

/**
 * Screen 04 · Impartiality and liability.
 *
 * ISO/IEC 17021-1 Cl. 5 requires a certification body to safeguard impartiality and to
 * hold adequate liability arrangements. Both answers are `boolean not null`, so neither
 * question carries a default — an unanswered question is not the same fact as a No, and a
 * pre-selected radio would record an answer nobody gave.
 *
 * Neither answer refuses the application. A missing impartiality committee stops
 * *activation*, and declared consultancy drives a per-client declaration at intake; both
 * come back as advisories that the verifying administrator reads.
 */
export function ImpartialityStep({
  draft,
  set,
  errorFor,
}: {
  draft: ApplicationDraft;
  set: (patch: Partial<ApplicationDraft>) => void;
  errorFor: (field: string) => string | undefined;
}) {
  return (
    <section className="card">
      <h2>Impartiality and liability</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        ISO/IEC 17021-1 Cl. 5 requires a certification body to safeguard impartiality and to
        hold adequate liability arrangements. These answers are recorded and visible to a
        Complao reviewer.
      </p>

      <BooleanChoice
        id="impartiality_committee"
        label="Do you operate a committee for safeguarding impartiality?"
        hint="ISO/IEC 17021-1 Cl. 5.2"
        value={draft.impartiality_committee ?? null}
        onChange={(value) => set({ impartiality_committee: value })}
        error={errorFor("impartiality_committee")}
        required
      />

      <BooleanChoice
        id="consultancy_offered"
        label="Does your organisation offer management system consultancy?"
        hint="ISO/IEC 17021-1 Cl. 5.2.5"
        value={draft.consultancy_offered ?? null}
        onChange={(value) => set({ consultancy_offered: value })}
        error={errorFor("consultancy_offered")}
        negativeFirst
        required
      />

      <TextAreaField
        id="liability_cover"
        label="Liability arrangements"
        hint="ISO/IEC 17021-1 Cl. 5.1.2 — insurer, cover and expiry"
        placeholder="e.g. Chubb — professional indemnity AUD 15m, expires 2027-09-30"
        value={draft.liability_cover ?? ""}
        onChange={(value) => set({ liability_cover: value })}
        error={errorFor("liability_cover")}
        required
      />
    </section>
  );
}
